<template>
  <OverviewCard
    :title="$t('key_dashboard_order_status')"
    :loading="loading"
    :error="error"
    :empty="!data.total"
  >
    <div class="d-flex flex-wrap justify-content-center align-items-center gap-4">
      <div class="donut" @mouseleave="hovered = null">
        <canvas ref="canvas"></canvas>
        <div class="donut-center">
          <h4 class="mb-0 font-weight-bolder">{{ format(hovered ? hovered.count : data.total) }}</h4>
          <p class="text-xs text-secondary mb-0">
            {{ hovered ? $t(`key_dashboard_group_${hovered.key}`) : $t("key_dashboard_orders_unit") }}
          </p>
        </div>
      </div>
      <ul class="legend list-unstyled mb-0">
        <li
          v-for="group in data.groups"
          :key="group.key"
          class="d-flex align-items-center justify-content-between text-xs mb-2"
        >
          <span class="legend-label d-flex align-items-center text-secondary">
            <span class="legend-dot flex-shrink-0 me-2" :style="{ backgroundColor: groupColors[group.key] }"></span>
            {{ $t(`key_dashboard_group_${group.key}`) }}
          </span>
          <span class="font-weight-bold flex-shrink-0 ms-3">{{ percent(group.percent) }}</span>
        </li>
      </ul>
    </div>
  </OverviewCard>
</template>

<script>
import Chart from "chart.js/auto";
import OverviewCard from "./OverviewCard.vue";
import { COLORS, formatNumber, formatPercent } from "./shared";

const GROUP_COLORS = {
  completed_not_dispatched: COLORS.greenMid,
  completed_dispatched: COLORS.green,
  in_preparation: COLORS.greenLight,
  pending: COLORS.yellow,
  blocked: COLORS.red,
};

export default {
  name: "OrderStatusChart",
  components: { OverviewCard },
  props: {
    data: { type: Object, default: () => ({ total: 0, groups: [] }) },
    loading: { type: Boolean, default: false },
    error: { type: [Object, Error, String], default: null },
  },
  data() {
    return { groupColors: GROUP_COLORS, hovered: null };
  },
  watch: {
    data() {
      this.hovered = null;
      // The canvas only exists once there is data to draw.
      this.$nextTick(this.drawChart);
    },
  },
  mounted() {
    this.drawChart();
  },
  beforeUnmount() {
    this.chart?.destroy();
  },
  methods: {
    format(value) {
      return formatNumber(value, this.$i18n.locale);
    },
    percent(value) {
      return formatPercent(value, this.$i18n.locale);
    },
    drawChart() {
      this.chart?.destroy();
      this.chart = null;
      if (!this.$refs.canvas || !this.data.total) {
        return;
      }
      this.chart = new Chart(this.$refs.canvas.getContext("2d"), {
        type: "doughnut",
        data: {
          labels: this.data.groups.map((group) => this.$t(`key_dashboard_group_${group.key}`)),
          datasets: [
            {
              data: this.data.groups.map((group) => group.count),
              backgroundColor: this.data.groups.map((group) => GROUP_COLORS[group.key]),
              borderWidth: 0,
            },
          ],
        },
        options: {
          cutout: "68%",
          responsive: true,
          maintainAspectRatio: false,
          // The canvas tooltip is clipped by the small canvas and hides the
          // total, so the hovered group is shown in the centre instead.
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
          onHover: (event, elements) => {
            this.hovered = elements.length ? this.data.groups[elements[0].index] : null;
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.donut {
  position: relative;
  width: 160px;
  height: 160px;
}

.donut-center {
  position: absolute;
  inset: 0;
  padding: 0 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* Next to the donut while it fits, below it otherwise. */
.legend {
  flex: 1 1 11rem;
  min-width: 0;
}

.legend-label {
  min-width: 0;
}

.legend-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  display: inline-block;
}
</style>
